import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  History, 
  RefreshCw, 
  User,
  FileText,
  Users,
  CreditCard,
  Plus,
  Edit,
  Trash2,
  ArrowRight,
  ChevronDown
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuditLogsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [expandedLogs, setExpandedLogs] = useState({});

  useEffect(() => {
    fetchLogs();
  }, [entityFilter, actionFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (entityFilter) params.append('entity_type', entityFilter);
      if (actionFilter) params.append('action', actionFilter);
      
      const response = await axios.get(`${API}/admin/audit-logs?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      setLogs(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (logId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getActionBadge = (action) => {
    const config = {
      CREATE: { class: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Plus },
      UPDATE: { class: 'bg-blue-50 text-blue-700 border-blue-200', icon: Edit },
      DELETE: { class: 'bg-red-50 text-red-700 border-red-200', icon: Trash2 },
      STATUS_CHANGE: { class: 'bg-amber-50 text-amber-700 border-amber-200', icon: ArrowRight },
      EXPORT: { class: 'bg-purple-50 text-purple-700 border-purple-200', icon: FileText }
    };
    const actionConfig = config[action] || config.UPDATE;
    const Icon = actionConfig.icon;
    return (
      <Badge className={`${actionConfig.class} border font-medium flex items-center gap-1 px-2 py-1`}>
        <Icon className="w-3 h-3" />
        {action}
      </Badge>
    );
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      EXPENSE: FileText,
      USER: Users,
      PAYMENT: CreditCard
    };
    return icons[entityType] || FileText;
  };

  const renderChanges = (changes) => {
    if (!changes) return null;
    
    // Handle old/new format
    if (changes.old && changes.new) {
      return (
        <div className="space-y-2">
          {Object.keys(changes.new).map(key => (
            <div key={key} className="flex items-center gap-2 text-sm">
              <span className="font-medium text-slate-600 min-w-[100px]">{key}:</span>
              <span className="text-red-600 line-through">{JSON.stringify(changes.old[key])}</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <span className="text-emerald-600">{JSON.stringify(changes.new[key])}</span>
            </div>
          ))}
        </div>
      );
    }
    
    // Handle deleted entity
    if (changes.deleted || changes.deleted_user) {
      const deleted = changes.deleted || changes.deleted_user;
      return (
        <div className="space-y-1 text-sm">
          <p className="text-red-600 font-medium">Deleted:</p>
          {Object.entries(deleted).map(([key, value]) => (
            <div key={key} className="flex items-center gap-2 text-slate-600">
              <span className="font-medium">{key}:</span>
              <span>{JSON.stringify(value)}</span>
            </div>
          ))}
        </div>
      );
    }
    
    // Handle new entity
    if (changes.email || changes.full_name) {
      return (
        <div className="space-y-1 text-sm">
          {Object.entries(changes).filter(([k, v]) => v !== undefined).map(([key, value]) => (
            <div key={key} className="flex items-center gap-2 text-slate-600">
              <span className="font-medium">{key}:</span>
              <span className="text-emerald-600">{JSON.stringify(value)}</span>
            </div>
          ))}
        </div>
      );
    }
    
    // Handle status change
    if (changes.old_status && changes.new_status) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-slate-600">Status:</span>
          <Badge variant="outline" className="text-red-600">{changes.old_status}</Badge>
          <ArrowRight className="w-4 h-4 text-slate-400" />
          <Badge variant="outline" className="text-emerald-600">{changes.new_status}</Badge>
        </div>
      );
    }
    
    // Default: show raw JSON
    return (
      <pre className="text-xs bg-slate-50 p-2 rounded overflow-x-auto">
        {JSON.stringify(changes, null, 2)}
      </pre>
    );
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-8 text-center">
          <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-700">Admin Access Required</h2>
          <p className="text-slate-500 text-sm mt-2">Only administrators can view audit logs.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950 flex items-center gap-3">
            <History className="w-7 h-7" />
            Audit Logs
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Track all changes made in the system
          </p>
        </div>
        <Button onClick={fetchLogs} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <label className="text-sm font-medium text-slate-700 mb-2 block">Entity Type</label>
              <Select value={entityFilter} onValueChange={(val) => setEntityFilter(val === 'all' ? '' : val)}>
                <SelectTrigger data-testid="entity-filter">
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="EXPENSE">Expenses</SelectItem>
                  <SelectItem value="USER">Users</SelectItem>
                  <SelectItem value="PAYMENT">Payments</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-slate-700 mb-2 block">Action</label>
              <Select value={actionFilter} onValueChange={(val) => setActionFilter(val === 'all' ? '' : val)}>
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="CREATE">Create</SelectItem>
                  <SelectItem value="UPDATE">Update</SelectItem>
                  <SelectItem value="DELETE">Delete</SelectItem>
                  <SelectItem value="STATUS_CHANGE">Status Change</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
            </div>
          ) : logs.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {logs.map((log, index) => {
                const EntityIcon = getEntityIcon(log.entity_type);
                const isExpanded = expandedLogs[log.id];
                
                return (
                  <Collapsible key={log.id} open={isExpanded} onOpenChange={() => toggleExpand(log.id)}>
                    <div 
                      className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                      data-testid={`audit-log-${index}`}
                    >
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <EntityIcon className="w-5 h-5 text-slate-600" />
                            </div>
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                {getActionBadge(log.action)}
                                <span className="text-sm font-medium text-slate-900">
                                  {log.entity_type}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                by <span className="font-medium">{log.user_name || 'System'}</span>
                                {log.user_email && <span className="text-slate-400"> ({log.user_email})</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-mono text-sm text-slate-600">{formatDate(log.timestamp)}</p>
                              <p className="text-xs text-slate-400 font-mono">ID: {log.entity_id?.slice(0, 8)}...</p>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="mt-4 pl-14 pr-4 pb-2">
                          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Changes</p>
                            {renderChanges(log.changes)}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          ) : (
            <div className="py-16 text-center">
              <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-1">No audit logs yet</h3>
              <p className="text-slate-500 text-sm">Logs will appear here when changes are made.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
